/**
 * MobileContext
 *
 * Global context for mobile-specific state management.
 * Handles sidebar open/close state, touch interactions, and mobile UI state.
 *
 * Usage:
 *   <MobileProvider>
 *     <App />
 *   </MobileProvider>
 *
 *   const { isSidebarOpen, toggleSidebar } = useMobile();
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';

interface MobileContextValue {
  /** Whether the campaign sidebar/drawer is open */
  isSidebarOpen: boolean;
  /** Toggle campaign sidebar open/close */
  toggleSidebar: () => void;
  /** Open campaign sidebar */
  openSidebar: () => void;
  /** Close campaign sidebar */
  closeSidebar: () => void;
  /** Whether the app navigation sidebar is open (mobile drawer) */
  isAppSidebarOpen: boolean;
  /** Toggle app navigation sidebar */
  toggleAppSidebar: () => void;
  /** Close app navigation sidebar */
  closeAppSidebar: () => void;
  /** Whether the device is mobile (< 768px) */
  isMobile: boolean;
  /** Whether the device is tablet (768px - 1023px) */
  isTablet: boolean;
  /** Whether the sidebar should be shown as a drawer (mobile/tablet) */
  shouldShowDrawer: boolean;
  /** Whether keyboard is visible (mobile virtual keyboard) */
  isKeyboardVisible: boolean;
  /** Set keyboard visibility */
  setKeyboardVisible: (visible: boolean) => void;
}

const MobileContext = createContext<MobileContextValue | undefined>(undefined);

interface MobileProviderProps {
  children: React.ReactNode;
}

export const MobileProvider: React.FC<MobileProviderProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAppSidebarOpen, setIsAppSidebarOpen] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Sidebar should be drawer on mobile and tablet
  const shouldShowDrawer = isMobile || isTablet;

  // Close sidebars when switching to desktop
  useEffect(() => {
    if (!shouldShowDrawer) {
      setIsSidebarOpen(false);
      setIsAppSidebarOpen(false);
    }
  }, [shouldShowDrawer]);

  // Close sidebars on route change (mobile navigation)
  useEffect(() => {
    const handleRouteChange = () => {
      if (shouldShowDrawer) {
        setIsSidebarOpen(false);
        setIsAppSidebarOpen(false);
      }
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [shouldShowDrawer]);

  // Handle escape key to close sidebars
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isSidebarOpen) setIsSidebarOpen(false);
        if (isAppSidebarOpen) setIsAppSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSidebarOpen, isAppSidebarOpen]);

  // Prevent body scroll when any sidebar is open on mobile
  useEffect(() => {
    if ((isSidebarOpen || isAppSidebarOpen) && shouldShowDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen, isAppSidebarOpen, shouldShowDrawer]);

  // Keyboard visibility detection for mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleResize = () => {
      // On mobile, if viewport height is significantly less than screen height,
      // the virtual keyboard is likely open
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const screenHeight = window.screen.height;
      const threshold = screenHeight * 0.7; // If viewport is less than 70% of screen, keyboard is likely open

      setIsKeyboardVisible(viewportHeight < threshold);
    };

    // Use visualViewport API if available (more reliable)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [isMobile]);

  // When opening one sidebar, close the other to avoid overlap on mobile
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => {
      if (!prev) setIsAppSidebarOpen(false);
      return !prev;
    });
  }, []);

  const openSidebar = useCallback(() => {
    setIsAppSidebarOpen(false);
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const toggleAppSidebar = useCallback(() => {
    setIsAppSidebarOpen(prev => {
      if (!prev) setIsSidebarOpen(false);
      return !prev;
    });
  }, []);

  const closeAppSidebar = useCallback(() => {
    setIsAppSidebarOpen(false);
  }, []);

  const setKeyboardVisible = useCallback((visible: boolean) => {
    setIsKeyboardVisible(visible);
  }, []);

  const value: MobileContextValue = {
    isSidebarOpen,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    isAppSidebarOpen,
    toggleAppSidebar,
    closeAppSidebar,
    isMobile,
    isTablet,
    shouldShowDrawer,
    isKeyboardVisible,
    setKeyboardVisible,
  };

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
};

/**
 * Hook to access mobile context
 */
export function useMobile(): MobileContextValue {
  const context = useContext(MobileContext);
  if (context === undefined) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
}

export default MobileContext;
